// The kit's own modal shell and confirmation dialog — used when the host's
// DesignerCatalog injects none (the downloaded / J2EE runtime). Themed with the
// shared `--color-*` tokens so it matches the surface it opens on, portalled to
// <body> so a scrolling property panel never clips it, closed on Escape and on
// a backdrop click.

import { useCallback, useEffect, useRef, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle, X } from 'lucide-react'
import type { DesignerCatalog, KitConfirmOptions, KitModalProps } from './catalog'

const WIDTH = { default: 560, wide: 760, full: 980 } as const

export function KitModal({ open, onClose, title, children, footer, size = 'default' }: KitModalProps) {
  useEffect(() => {
    if (!open) return undefined
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])
  if (!open || typeof document === 'undefined') return null
  return createPortal(
    <div onMouseDown={onClose} style={scrim}>
      <div role="dialog" aria-modal="true" aria-label={title || 'Dialog'}
        onMouseDown={(e) => e.stopPropagation()}
        style={{ ...card, width: `min(${WIDTH[size]}px, calc(100vw - 24px))` }}>
        <div style={head}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 600, flex: 1, minWidth: 0 }}>{title}</h2>
          <button type="button" onClick={onClose} aria-label="Close" style={iconBtn}><X size={16} /></button>
        </div>
        <div style={body}>{children}</div>
        {footer && <div style={foot}>{footer}</div>}
      </div>
    </div>,
    document.body,
  )
}

/** The modal a catalog asks for (its own, else the kit's). */
export function modalOf(catalog: DesignerCatalog | null | undefined) {
  return catalog?.Modal || KitModal
}

function KitConfirmDialog({ opts, onAnswer, Modal }: {
  opts: KitConfirmOptions | null
  onAnswer: (ok: boolean) => void
  Modal: ReturnType<typeof modalOf>
}) {
  const okRef = useRef<HTMLButtonElement | null>(null)
  useEffect(() => { if (opts) okRef.current?.focus() }, [opts])
  const danger = (opts?.tone || 'danger') === 'danger'
  return (
    <Modal open={!!opts} onClose={() => onAnswer(false)} title={opts?.title || 'Are you sure?'}
      footer={(
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', width: '100%' }}>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => onAnswer(false)}>
            {opts?.cancelLabel || 'Cancel'}
          </button>
          <button ref={okRef} type="button" className="btn btn-primary btn-sm"
            style={danger ? { background: 'var(--color-error)', borderColor: 'var(--color-error)' } : undefined}
            onClick={() => onAnswer(true)}>
            {opts?.confirmLabel || 'Confirm'}
          </button>
        </div>
      )}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 14, lineHeight: 1.5 }}>
        {danger && <AlertTriangle size={18} style={{ color: 'var(--color-error)', flexShrink: 0, marginTop: 2 }} />}
        <div>{opts?.message}</div>
      </div>
    </Modal>
  )
}

/** `{confirm, confirmDialog}` — the catalog's confirm when it has one (then
 *  `confirmDialog` is null), else the kit's own dialog, which the caller
 *  renders. A second question while one is open answers the first "no". */
export function useKitConfirm(catalog: DesignerCatalog | null | undefined): {
  confirm: (opts: KitConfirmOptions) => Promise<boolean>
  confirmDialog: ReactNode
} {
  const [opts, setOpts] = useState<KitConfirmOptions | null>(null)
  const resolveRef = useRef<((ok: boolean) => void) | null>(null)
  const hostConfirm = catalog?.confirm
  const settle = useCallback((ok: boolean) => {
    setOpts(null)
    const r = resolveRef.current
    resolveRef.current = null
    r?.(ok)
  }, [])
  const own = useCallback((o: KitConfirmOptions): Promise<boolean> => {
    resolveRef.current?.(false)
    setOpts(o)
    return new Promise<boolean>((resolve) => { resolveRef.current = resolve })
  }, [])
  const confirm = useCallback((o: KitConfirmOptions): Promise<boolean> => {
    if (!hostConfirm) return own(o)
    return hostConfirm(o).catch(() => false)
  }, [hostConfirm, own])
  const Modal = modalOf(catalog)
  return {
    confirm,
    confirmDialog: hostConfirm ? null : <KitConfirmDialog opts={opts} onAnswer={settle} Modal={Modal} />,
  }
}

const scrim: CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 100000, background: 'rgba(16,24,40,0.32)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12,
}
const card: CSSProperties = {
  maxHeight: 'calc(100vh - 32px)', display: 'flex', flexDirection: 'column', overflow: 'hidden',
  background: 'var(--color-bg)', color: 'var(--color-text)', borderRadius: 12,
  border: '1px solid var(--color-border)', boxShadow: '0 10px 30px rgba(16,24,40,0.18)',
  fontFamily: "'IBM Plex Sans', system-ui, -apple-system, 'Segoe UI', sans-serif",
}
const head: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px',
  borderBottom: '1px solid var(--color-border)',
}
const body: CSSProperties = { overflow: 'auto', padding: 16, fontSize: 14 }
const foot: CSSProperties = {
  padding: '10px 16px', borderTop: '1px solid var(--color-border)', display: 'flex',
  gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap',
}
const iconBtn: CSSProperties = {
  display: 'inline-grid', placeItems: 'center', width: 28, height: 28, borderRadius: 6,
  border: 'none', background: 'transparent', color: 'var(--color-text-secondary)', cursor: 'pointer',
}
